import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "scroll",
    "acceptButton",
    "termsBottom",
    "scrollToBottomButton",
    "customScrollbar",
    "customThumb",
  ];
  static values = { threshold: { type: Number, default: 12 } };

  connect() {
    if (!this.hasScrollTarget || !this.hasAcceptButtonTarget) return;

    this.unlocked = false;

    this.onScroll = this.onScroll.bind(this);
    this.scrollTarget.addEventListener("scroll", this.onScroll, { passive: true });
    this.onResize = this.onResize.bind(this);
    window.addEventListener("resize", this.onResize, { passive: true });

    if (this.hasTermsBottomTarget) {
      this.io = new IntersectionObserver(
        (entries) => {
          const visible = entries[0]?.isIntersecting;
          if (visible) {
            console.log("[terms] termsBottom is visible");
            this.unlock();
          }
        },
        {
          root: this.scrollTarget,
          threshold: 0.01,
        }
      );

      this.io.observe(this.termsBottomTarget);
    }

    this.setupCustomScrollbar();
    this.syncState();
    this.isScrollbarSyncActive = true;
    this.scrollbarTick();
  }

  disconnect() {
    if (this.hasScrollTarget) {
      this.scrollTarget.removeEventListener("scroll", this.onScroll);
    }
    window.removeEventListener("resize", this.onResize);
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.isScrollbarSyncActive = false;
    if (this.scrollbarRafId) {
      cancelAnimationFrame(this.scrollbarRafId);
      this.scrollbarRafId = null;
    }
  }

  onScroll() {
    this.syncState();
    this.updateCustomScrollbar();
  }

  onResize() {
    this.updateCustomScrollbar();
  }

  setupCustomScrollbar() {
    if (!this.hasCustomScrollbarTarget || !this.hasCustomThumbTarget || !this.hasScrollTarget) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateCustomScrollbar();
    });

    this.resizeObserver.observe(this.scrollTarget);
    const scrollContent = this.scrollTarget.firstElementChild;
    if (scrollContent) {
      this.resizeObserver.observe(scrollContent);
    }
  }

  updateCustomScrollbar() {
    if (!this.hasCustomScrollbarTarget || !this.hasCustomThumbTarget || !this.hasScrollTarget) return;

    const scrollEl = this.scrollTarget;
    const trackEl = this.customScrollbarTarget;
    const thumbEl = this.customThumbTarget;

    const verticalOffsetTop = 80;
    const verticalOffsetBottom = 108;
    const trackTop = scrollEl.offsetTop + verticalOffsetTop;
    const trackHeightFromViewport = Math.max(0, scrollEl.clientHeight - verticalOffsetTop - verticalOffsetBottom);

    const scrollHeight = scrollEl.scrollHeight;
    const clientHeight = scrollEl.clientHeight;
    const canScroll = scrollHeight > clientHeight + 1;

    if (trackHeightFromViewport <= 0) return;

    trackEl.style.top = `${trackTop}px`;
    trackEl.style.height = `${trackHeightFromViewport}px`;
    trackEl.style.display = "block";
    trackEl.style.opacity = canScroll ? "1" : "0.55";

    const trackHeight = trackEl.clientHeight;
    const minThumbHeight = 30;
    const thumbHeight = canScroll
      ? Math.max(minThumbHeight, (clientHeight / scrollHeight) * trackHeight)
      : Math.max(minThumbHeight, trackHeight);
    const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
    const maxScrollTop = Math.max(1, scrollHeight - clientHeight);
    const thumbTop = canScroll ? (scrollEl.scrollTop / maxScrollTop) * maxThumbTop : 0;

    thumbEl.style.height = `${thumbHeight}px`;
    thumbEl.style.transform = `translateY(${thumbTop}px)`;
  }

  scrollbarTick() {
    if (!this.isScrollbarSyncActive) return;
    this.updateCustomScrollbar();
    this.scrollbarRafId = requestAnimationFrame(() => this.scrollbarTick());
  }

  async scrollToTermsBottom(event) {
    event.preventDefault();
    if (!this.hasTermsBottomTarget || !this.hasScrollTarget) return;

    const extraOffset = 120;
    const scrollEl = this.scrollTarget;
    const scrollRect = scrollEl.getBoundingClientRect();
    const targetRect = this.termsBottomTarget.getBoundingClientRect();

    const targetTopInScroll = targetRect.top - scrollRect.top + scrollEl.scrollTop;
    const targetBottomInScroll = targetTopInScroll + this.termsBottomTarget.offsetHeight;
    const endAlignedTop = targetBottomInScroll - scrollEl.clientHeight;

    const desiredTop = Math.min(
      scrollEl.scrollHeight - scrollEl.clientHeight,
      Math.max(0, endAlignedTop + extraOffset),
    );

    scrollEl.scrollTo({
      top: desiredTop,
      behavior: "smooth",
    });

    await this.waitForScrollFinish(scrollEl, desiredTop);
    if (this.hasScrollToBottomButtonTarget) {
      this.scrollToBottomButtonTarget.classList.add("hidden");
    }
  }

  waitForScrollFinish(scrollEl, desiredTop) {
    return new Promise((resolve) => {
      const maxWaitMs = 1200;
      const start = performance.now();
      const done = () => resolve();

      const tick = () => {
        const reached = Math.abs(scrollEl.scrollTop - desiredTop) <= 2;
        const timedOut = performance.now() - start > maxWaitMs;

        if (reached || timedOut) {
          done();
          return;
        }

        requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    });
  }

  unlock() {
    if (this.unlocked) return;
    this.unlocked = true;

    const btn = this.acceptButtonTarget;
    btn.disabled = false;
    btn.classList.remove(
      "pointer-events-none",
      "backdrop-blur-lg", "bg-white/10", "wlight:bg-[#1c197b]/10",
      "wdark:text-wdark-text-and-title", "wlight:text-wlight-text-and-title", "dark:text-dark-text-and-title",
    );
    btn.classList.add(
      "cursor-pointer",
      "wdark:bg-wdark-button-background", "wlight:bg-wlight-button-background", "dark:bg-dark-button-background",
      "wdark:text-wdark-primary-icon-and-text", "wlight:text-wlight-primary-icon-and-text", "dark:text-dark-primary-icon-and-text",
    );

    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
  }

  syncState() {
    if (this.unlocked) return;

    const el = this.scrollTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom <= this.thresholdValue;

    if (atBottom) {
      this.unlock();
    }
  }
}
